/**
 * =============================================================================
 * DEBOUNCE HOOKS - Input and callback debouncing
 * =============================================================================
 * 
 * Provides debouncing utilities to prevent excessive function calls,
 * particularly useful for search inputs, API calls, and resize handlers.
 * 
 * FEATURES:
 * - Value debouncing: Delay updates until user stops typing
 * - Callback debouncing: Delay function execution
 * - Automatic cleanup on unmount
 * 
 * USAGE:
 * ```tsx
 * // Debounce a search term (waits 300ms after typing stops)
 * const [searchTerm, setSearchTerm] = useState("");
 * const debouncedSearch = useDebounce(searchTerm, 300);
 * 
 * useEffect(() => {
 *   if (debouncedSearch) fetchResults(debouncedSearch);
 * }, [debouncedSearch]);
 * 
 * // Debounce a callback function
 * const debouncedSave = useDebouncedCallback((data) => {
 *   saveToServer(data);
 * }, 500);
 * ```
 * 
 * PERFORMANCE:
 * - Reduces API calls during rapid input
 * - Prevents UI jank from frequent re-renders
 * - Improves perceived responsiveness
 * 
 * @see src/components/SearchInput.tsx - Can use for search suggestions
 * =============================================================================
 */

import { useState, useEffect } from "react";

// =============================================================================
// VALUE DEBOUNCE
// =============================================================================

/**
 * Debounce a value - returns the value after a delay.
 * 
 * @param value - The value to debounce
 * @param delay - Delay in milliseconds (default: 300ms)
 * @returns The debounced value
 */
export function useDebounce<T>(value: T, delay = 300): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

// =============================================================================
// CALLBACK DEBOUNCE
// =============================================================================

/**
 * Debounce a callback function.
 * The callback will only execute after the specified delay
 * since the last invocation.
 * 
 * @param callback - The function to debounce
 * @param delay - Delay in milliseconds (default: 300ms)
 * @returns The debounced function
 */
export function useDebouncedCallback<T extends (...args: any[]) => any>(
  callback: T,
  delay = 300
): T {
  const [timeoutId, setTimeoutId] = useState<ReturnType<typeof setTimeout> | null>(null);

  const debouncedCallback = ((...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const newTimeoutId = setTimeout(() => {
      callback(...args);
    }, delay);

    setTimeoutId(newTimeoutId);
  }) as T;

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [timeoutId]);

  return debouncedCallback;
}
